import { Card, Center, Loader } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'

import { EmptyState } from '@/components/EmptyState'
import { PricingTiersDisplay } from '@/components/PricingTiersDisplay'
import { useGlobalState } from '@/hooks/useGlobalState'

export function PricingTab() {
  const { api } = useGlobalState()

  const plansQuery = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.getPlans(),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const billingInfoQuery = useQuery({
    queryKey: ['billing', 'info'],
    queryFn: () => api.getBillingInfo(),
  })

  // Only the tiers gate this screen. billingInfo supplies the optional user count, and it is
  // slow to fail, so waiting on it holds back tiers that are already in hand.
  // isPending, not isLoading: a query paused while offline is not fetching, and falling
  // through with no data would claim the account has no tiers.
  if (plansQuery.isPending) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    )
  }

  // isLoadingError, not isError: a failed refetch still has good tiers cached, so only an
  // initial load failure is worth an error screen. A failed billingInfo costs the optional
  // user count and nothing else.
  if (plansQuery.isLoadingError) {
    return (
      <Card shadow="sm" p="xl" radius="md" withBorder>
        <EmptyState
          Icon={IconAlertTriangle}
          title="Pricing information unavailable"
          description="Something went wrong while loading the pricing tiers. Please refresh the page, or contact support if the problem continues."
        />
      </Card>
    )
  }

  const billingInfo = billingInfoQuery.data?.billingInfo

  return (
    <Card shadow="sm" p="xl" radius="md" withBorder>
      <PricingTiersDisplay plans={plansQuery.data || []} currentUserCount={billingInfo?.currentSubscribers} />
    </Card>
  )
}
